import {
	OrderShippingState,
	OrderStatus,
	useGetAllOrdersQuery,
	useGetOrderOverviewStatsQuery,
	type GetAllOrdersQuery,
} from '@codezo/graphql';
import { ListFooter, PagingModeToggle, usePagedList, type RowEstimate } from '@codezo/lists';
import { FileText, Filter, MoreHorizontal, Pencil, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetPageMeta } from '../../hooks/useSetPageMeta';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { DatePicker } from '../../components/ui/date-picker';
import { OrganizationMultiSelector } from '../../components/selectors/OrganizationMultiSelector';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { SortableTableHead } from '../../components/ui/sortable-table-head';
import { StatusBadge } from '../../components/ui/status-badge';
import { Tabs } from '../../components/ui/tabs';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../../components/ui/table';
import { CurrencyText } from '../../components/CurrencyText';
import { SearchInput } from '../../components/ui/search-input';
import { cn, formatDate } from '../../lib/utils';
import { ORDER_SHIPPING_STATE, ORDER_STATUS, type StatusConfig } from '../../lib/status-configs';
import { useListParams } from '../../hooks/useListParams';
import { useQueryParam, useQueryParamDate, useQueryParamList } from '../../hooks/useQueryParam';
import { EmptyState } from '../../components/ui/empty-state';
import { TableSkeleton } from '../../components/ui/list-skeletons';
import { useCurrencyStore } from '../../stores/currency';

function OrderStatusBadge({ status }: { status: string }) {
	const config = ORDER_STATUS[status] ?? { label: status, tone: 'neutral' as const };
	return <StatusBadge active={true} activeLabel={config.label} activeTone={config.tone} />;
}

type OrderShipping = NonNullable<GetAllOrdersQuery['getAllOrders']['data'][number]['shipping']>;

/* Where the order's boxes are. The badge says what needs doing; the line under it
   says how far along it is, which a single state can't for a split order. */
function OrderShippingCell({ shipping }: { shipping?: OrderShipping | null }) {
	if (!shipping || shipping.state === OrderShippingState.NoShipments) {
		return <span className="text-text-4">—</span>;
	}
	const config = ORDER_SHIPPING_STATE[shipping.state] ?? {
		label: shipping.state,
		tone: 'neutral' as const,
	};
	return (
		<div className="flex flex-col items-start gap-1">
			<StatusBadge active={true} activeLabel={config.label} activeTone={config.tone} />
			{shipping.total > 1 && (
				<span className="text-xs text-text-3">
					{shipping.shipped}/{shipping.total} shipped
				</span>
			)}
			{shipping.overdue > 0 && (
				<span className="text-xs font-medium text-status-danger">
					{shipping.overdue} overdue
				</span>
			)}
		</div>
	);
}

type OrderPayment = NonNullable<
	GetAllOrdersQuery['getAllOrders']['data'][number]['paymentSummary']
>;

/* Products and samples together, gross — the same figure as the Total column, so
   "paid" here means the client owes nothing on the order. */
function OrderPaymentCell({ payment }: { payment?: OrderPayment | null }) {
	if (!payment || payment.totalAmount <= 0) {
		return <span className="text-text-4">—</span>;
	}
	const { label, tone }: StatusConfig =
		payment.totalOutstandingAmount <= 0
			? { label: 'Paid', tone: 'success' }
			: payment.totalPaidAmount > 0
				? { label: 'Part paid', tone: 'info' }
				: { label: 'Unpaid', tone: 'warn' };
	return (
		<div className="flex flex-col items-start gap-1">
			<StatusBadge active={true} activeLabel={label} activeTone={tone} />
			{payment.totalPaidAmount > 0 && payment.totalOutstandingAmount > 0 && (
				<span className="text-xs text-text-3">
					<CurrencyText amount={payment.totalOutstandingAmount} /> due
				</span>
			)}
		</div>
	);
}

/* Tabs that filter on shipping rather than order status. */
const SHIPPING_TABS: string[] = [OrderShippingState.ReadyToShip, OrderShippingState.InTransit];

/**
 * One table row, for sizing scroll-mode batches: the two-line name cell (13px name over an
 * 11px order number, at the default 1.5 line height) or a delivery badge with its progress
 * line under it, plus cell padding and the row border.
 */
function orderRows(): RowEstimate {
	return { rowHeight: 60 };
}

/* ════════════════════════ ORDERS PAGE ════════════════════════ */

export function OrdersPage() {
	const navigate = useNavigate();
	useSetPageMeta('Orders', [{ label: 'Orders' }]);
	const { selectedCurrency } = useCurrencyStore();
	const {
		search,
		setSearch,
		debouncedSearch,
		setPage,
		sortField,
		sortDirection,
		sortDir,
		toggleSort,
		limit,
		mode,
		setMode,
		request,
		loadMore,
	} = useListParams({ defaultSort: 'createdAt', defaultDir: 'desc', scrollRows: orderRows });
	const [tab, setTab] = useQueryParam('tab', 'all');
	const [filtersOpen, setFiltersOpen] = useState(false);
	/* In the URL, so a filtered list survives a reload and can be linked to —
	   which is what the client page's "See all" needs. */
	const [clientFilters, setClientFilters] = useQueryParamList('clients');
	const [createdFrom, setCreatedFrom] = useQueryParamDate('createdFrom');
	const [createdTo, setCreatedTo] = useQueryParamDate('createdTo');
	const [overdue, setOverdue] = useQueryParam('overdue', '');
	const overdueOnly = overdue === '1';

	const fmtDate = (d: Date) => formatDate(d);

	const removeFilter = (id: string) => {
		if (id === 'clients') setClientFilters([]);
		if (id === 'createdFrom') setCreatedFrom(null);
		if (id === 'createdTo') setCreatedTo(null);
		if (id === 'overdue') setOverdue('');
		setPage(1);
	};

	const clearAllFilters = () => {
		setClientFilters([]);
		setCreatedFrom(null);
		setCreatedTo(null);
		setOverdue('');
		setPage(1);
	};

	const activeFilters = (() => {
		const filters: Array<{ id: string; label: string }> = [];
		if (clientFilters.length > 0)
			filters.push({
				id: 'clients',
				label: `${clientFilters.length} client${clientFilters.length > 1 ? 's' : ''}`,
			});
		if (createdFrom)
			filters.push({ id: 'createdFrom', label: `Created from ${fmtDate(createdFrom)}` });
		if (createdTo)
			filters.push({ id: 'createdTo', label: `Created until ${fmtDate(createdTo)}` });
		if (overdueOnly) filters.push({ id: 'overdue', label: 'Overdue shipments' });
		return filters;
	})();

	const isShippingTab = SHIPPING_TABS.includes(tab);
	const statusFilter = tab === 'all' || isShippingTab ? undefined : [tab as OrderStatus];
	const shippingFilter = isShippingTab ? [tab as OrderShippingState] : undefined;

	const { data, loading } = useGetAllOrdersQuery({
		variables: {
			input: {
				pagination: request,
				...(debouncedSearch && { search: debouncedSearch }),
				order: { field: sortField, direction: sortDirection },
				statuses: statusFilter,
				shippingStates: shippingFilter,
				hasOverdueShipment: overdueOnly || undefined,
				organizationIds: clientFilters.length > 0 ? clientFilters : undefined,
				createdAt: createdFrom
					? {
							startDate: createdFrom.toISOString(),
							endDate: createdTo?.toISOString(),
						}
					: undefined,
			},
			inCurrency: selectedCurrency,
		},
		fetchPolicy: 'no-cache',
	});

	/* The orders query leaves `totalPages` out of its pageInfo; it follows from the
	   count. Memoised, as usePagedList tells a new result by identity. */
	const ordersPage = useMemo(() => {
		const result = data?.getAllOrders;
		return (
			result && {
				data: result.data,
				pageInfo: {
					...result.pageInfo,
					totalPages: Math.ceil(result.pageInfo.totalCount / limit),
				},
			}
		);
	}, [data, limit]);
	const list = usePagedList({ mode, request, limit, setPage, loadMore }, ordersPage, loading);
	const orders = list.items;

	const { data: statsData } = useGetOrderOverviewStatsQuery({ fetchPolicy: 'no-cache' });
	const stats = statsData?.getOrderOverviewStats;

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			{/* ── Header ── */}
			<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
				<div>
					<h1 className="mb-2 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
						Orders
					</h1>
					<p className="text-base text-text-3">
						Manage and track all orders across your organizations.
					</p>
				</div>
			</div>

			{/* ── KPI strip ── each card opens the list it counts. The counts are the whole
			    book, so a click clears search and filters — otherwise the list could show
			    fewer orders than the card promised. */}
			<div className="mb-7 grid grid-cols-2 divide-x divide-y divide-line-1 overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-sunken sm:grid-cols-3 lg:grid-cols-6">
				{[
					{
						label: 'Pending',
						value: stats?.pending ?? 0,
						sub: 'Awaiting production',
						tab: OrderStatus.Pending,
					},
					{
						label: 'In Production',
						value: stats?.inProduction ?? 0,
						sub: 'Being produced',
						tab: OrderStatus.InProduction,
					},
					{
						label: 'Ready to ship',
						value: stats?.readyToShip ?? 0,
						sub: 'Made, waiting to send',
						tab: OrderShippingState.ReadyToShip,
					},
					{
						label: 'In transit',
						value: stats?.inTransit ?? 0,
						sub: 'On the road',
						tab: OrderShippingState.InTransit,
					},
					{
						label: 'Overdue',
						value: stats?.overdue ?? 0,
						sub: 'Past estimated delivery',
						overdue: true,
					},
					{
						label: 'Completed',
						value: stats?.completed ?? 0,
						sub: 'Delivered',
						tab: OrderStatus.Completed,
					},
				].map((k) => (
					<button
						key={k.label}
						type="button"
						onClick={() => {
							clearAllFilters();
							setSearch('');
							setTab(k.tab ?? 'all');
							setOverdue(k.overdue ? '1' : '');
						}}
						className="cursor-pointer bg-transparent px-5 py-4 text-left transition-colors hover:bg-surface-hover"
					>
						<div className="mb-1.5 text-2xs font-medium uppercase tracking-[0.08em] text-text-4">
							{k.label}
						</div>
						<div
							className={cn(
								'mb-1 font-mono text-2xl font-medium',
								k.overdue && k.value > 0 ? 'text-status-danger' : 'text-text-1'
							)}
						>
							{k.value}
						</div>
						<div className="text-xs text-text-3">{k.sub}</div>
					</button>
				))}
			</div>

			{/* ── Tabs + filters row ── */}
			<div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
				<Tabs
					value={tab}
					onChange={(v) => {
						setTab(v);
						setPage(1);
					}}
					items={[
						{ value: 'all', label: 'All orders' },
						{ value: OrderStatus.Pending, label: 'Pending' },
						{ value: OrderStatus.InProduction, label: 'In Production' },
						{ value: OrderShippingState.ReadyToShip, label: 'Ready to ship' },
						{ value: OrderShippingState.InTransit, label: 'In transit' },
						{ value: OrderStatus.Completed, label: 'Completed' },
						{ value: OrderStatus.Cancelled, label: 'Cancelled' },
					]}
				/>
				<div className="flex items-center gap-2">
					<SearchInput
						value={search}
						onChange={setSearch}
						placeholder="Search orders..."
						className="flex-1 lg:flex-none lg:w-[200px]"
					/>

					{/* Filters popover */}
					<Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
						<PopoverTrigger
							className={cn(
								'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 text-sm transition-colors',
								filtersOpen
									? 'border-line-1 bg-surface-fill text-text-1'
									: 'border-transparent bg-transparent text-text-2 hover:text-text-1'
							)}
						>
							<Filter size={12} />
							Filters
							{activeFilters.length > 0 && (
								<span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 font-mono text-2xs font-semibold text-accent-fg">
									{activeFilters.length}
								</span>
							)}
						</PopoverTrigger>
						<PopoverContent align="end" className="w-[280px] gap-0 p-3">
							<div className="mb-3 flex items-center justify-between">
								<span className="text-sm font-medium text-text-1">Filters</span>
								<button
									onClick={clearAllFilters}
									className="cursor-pointer bg-transparent text-sm text-text-3 hover:text-text-1"
								>
									Reset
								</button>
							</div>

							{/* Client */}
							<div className="mb-3">
								<div className="mb-1.5 text-xs font-medium uppercase tracking-[0.06em] text-text-3">
									Client
								</div>
								<OrganizationMultiSelector
									value={clientFilters}
									onChange={(v) => {
										setClientFilters(v);
										setPage(1);
									}}
									placeholder="All clients"
								/>
							</div>

							{/* Created */}
							<div>
								<div className="mb-1.5 text-xs font-medium uppercase tracking-[0.06em] text-text-3">
									Created
								</div>
								<div className="flex items-center gap-1.5">
									<DatePicker
										variant="compact"
										value={createdFrom}
										onChange={(d) => {
											setCreatedFrom(d ?? null);
											setPage(1);
										}}
										placeholder="From"
									/>
									<span className="text-xs text-text-4">--</span>
									<DatePicker
										variant="compact"
										value={createdTo}
										onChange={(d) => {
											setCreatedTo(d ?? null);
											setPage(1);
										}}
										placeholder="To"
									/>
								</div>
							</div>

							{/* Shipping */}
							<div className="mt-3">
								<div className="mb-1.5 text-xs font-medium uppercase tracking-[0.06em] text-text-3">
									Shipping
								</div>
								<label className="flex cursor-pointer items-center gap-2 text-sm text-text-2">
									<Checkbox
										checked={overdueOnly}
										onCheckedChange={(checked) => {
											setOverdue(checked ? '1' : '');
											setPage(1);
										}}
									/>
									Only overdue shipments
								</label>
							</div>
						</PopoverContent>
					</Popover>
					<PagingModeToggle value={mode} onChange={setMode} />
				</div>
			</div>

			{/* ── Active filter chips ── */}
			{activeFilters.length > 0 && (
				<div className="mb-4 flex flex-wrap items-center gap-1.5">
					<span className="mr-1 text-xs text-text-3">Active filters</span>
					{activeFilters.map((f) => (
						<span
							key={f.id}
							className="inline-flex items-center gap-1 rounded-[var(--r-1)] border border-line-2 bg-surface py-0.5 pr-1 pl-2.5 text-sm text-text-2"
						>
							{f.label}
							<button
								onClick={() => removeFilter(f.id)}
								className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-[var(--r-1)] bg-transparent text-text-3 hover:text-text-1"
							>
								<X size={11} />
							</button>
						</span>
					))}
					<button
						onClick={clearAllFilters}
						className="cursor-pointer bg-transparent px-1.5 text-sm text-text-3 hover:text-text-1"
					>
						Clear all
					</button>
				</div>
			)}

			{/* ── Table ── */}
			{list.loading ? (
				<TableSkeleton />
			) : orders.length === 0 ? (
				<EmptyState
					icon={FileText}
					title="No orders found"
					description={
						search
							? 'Try a different search term.'
							: 'Orders will appear here once quotes are accepted.'
					}
				/>
			) : (
				<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="overflow-x-auto">
						<Table className="min-w-[1040px]">
							<TableHeader>
								<TableRow className="border-line-1 hover:bg-transparent">
									<SortableTableHead
										sortKey="name"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Name
									</SortableTableHead>
									<TableHead className="text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Client
									</TableHead>
									<TableHead className="text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Items
									</TableHead>
									<SortableTableHead
										sortKey="totalAmount"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[120px] text-right text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Total
									</SortableTableHead>
									<SortableTableHead
										sortKey="status"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[120px] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Status
									</SortableTableHead>
									<TableHead className="w-[140px] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Payment
									</TableHead>
									<TableHead className="w-[150px] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Delivery
									</TableHead>
									<SortableTableHead
										sortKey="createdAt"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[110px] text-right text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Created
									</SortableTableHead>
									<TableHead className="w-10" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{orders.map((order) => (
									<TableRow
										key={order.id}
										className="cursor-pointer border-line-1 transition-colors duration-[180ms] hover:bg-surface-hover last:border-b-0"
										onClick={() => navigate(`/orders/${order.id}`)}
									>
										<TableCell>
											<div className="text-base font-medium text-text-1">
												{order.name || '—'}
											</div>
											<div className="mt-0.5 font-mono text-xs text-text-4">
												{order.orderNumber}
											</div>
										</TableCell>
										<TableCell className="text-base text-text-2">
											{order.organization?.name || '—'}
										</TableCell>
										<TableCell className="text-base text-text-2">
											{order.items?.length ?? 0}
										</TableCell>
										<TableCell className="text-right text-base font-medium text-text-1">
											{/* What the client pays, VAT included — the order page's
											    headline too. No fallback: net and the old line-revenue
											    total answer different questions under the same column. */}
											{order.summary ? (
												<CurrencyText amount={order.summary.grossAmount} />
											) : (
												<span className="text-text-4">—</span>
											)}
										</TableCell>
										<TableCell>
											<OrderStatusBadge status={order.status} />
										</TableCell>
										<TableCell>
											<OrderPaymentCell payment={order.paymentSummary} />
										</TableCell>
										<TableCell>
											<OrderShippingCell shipping={order.shipping} />
										</TableCell>
										<TableCell className="text-right text-base text-text-3">
											{order.createdAt ? formatDate(order.createdAt) : '—'}
										</TableCell>
										<TableCell>
											<div className="flex justify-end">
												<DropdownMenu>
													<DropdownMenuTrigger
														onClick={(e) => e.stopPropagation()}
														className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
													>
														<MoreHorizontal size={14} />
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														<DropdownMenuItem
															onClick={(e) => {
																e.stopPropagation();
																navigate(`/orders/${order.id}`);
															}}
														>
															<Pencil size={14} /> View details
														</DropdownMenuItem>
														<DropdownMenuItem variant="destructive">
															<Trash2 size={14} /> Cancel order
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</Card>
			)}

			<ListFooter list={list} />
		</div>
	);
}
